import {
  ArrowClockwiseIcon,
  FileArchiveIcon,
  PlusSquareIcon,
  SpinnerIcon,
  TrashIcon,
  WarningDiamondIcon,
} from '@phosphor-icons/react';
import { filesize } from 'filesize';
import truncate from 'lodash/truncate';
import { ReactElement, useEffect, useState } from 'react';
import { Control, FieldValues, Path, useFormContext } from 'react-hook-form';

import CameraIcon from '@/assets/svgs/Camera';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

export type FilePickerProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  accept: string[];
  file: File | null;
  onFileChange: (key: string, file: File) => void;
  error?: string;
  maxFileSize?: number;
  tips?: ReactElement;
  placeholder?: string;
  isUploading?: boolean;
  isFileSizeEmpty?: boolean;
  withFilePreview?: boolean;
  onRetry?: () => void;
  useVideoIcon?: boolean;
  requiredMark?: boolean;
};

export const FilePicker = <T extends FieldValues>({
  name,
  label,
  control,
  tips,
  accept,
  error,
  onFileChange,
  file,
  onRetry,
  isUploading,
  maxFileSize,
  isFileSizeEmpty,
  useVideoIcon = false,
  withFilePreview = false,
  placeholder = 'Upload file',
  requiredMark,
}: FilePickerProps<T>) => {
  const testId = useGetTestId(name);

  const { setValue, setError } = useFormContext();
  const [filePreviewUrl, setFilePreviewUrl] = useState('');

  const getFileSizeValidationErrors = (file: File) => {
    return maxFileSize > 0 && file.size > maxFileSize
      ? `The uploaded file is too large. Maximum allowed size is ${filesize(maxFileSize, { precision: 1 })}`
      : '';
  };

  const pickFile = (event) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];
    const validationError = getFileSizeValidationErrors(file);

    if (!validationError) {
      onFileChange(name as string, file);
      setValue(name, file.name, { shouldValidate: true, shouldDirty: true });
    } else {
      setError(name, {
        message: validationError,
      });
    }
  };

  const clearFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setFilePreviewUrl('');
    onFileChange(name as string, null);
    setValue(name as string, '', { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setFilePreviewUrl(reader.result as string);
      };

      reader.readAsDataURL(file);
    }
  }, [file]);

  const handleRefresh = () => {
    onFileChange(name as string, file);
    onRetry?.();
  };

  const fileIcon = useVideoIcon ? (
    <CameraIcon />
  ) : (
    <FileArchiveIcon fill="white" size={36} />
  );

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem className="flex flex-col">
          {label && (
            <FormLabel
              htmlFor={`dropzone-file-${name}`}
              className="leading-[17px]"
            >
              {label}
              {requiredMark && <span className="text-red-500">*</span>}
            </FormLabel>
          )}

          <FormControl>
            <label
              htmlFor={`dropzone-file-${name}`}
              className={cn(
                'items-center justify-center mt-1 flex flex-col p-[20px] w-full border-2 border-zinc-700 border-dashed rounded-lg cursor-pointer bg-zinc-900',
                file && 'items-start',
                error && 'border-destructive border-solid'
              )}
            >
              {file ? (
                <>
                  <div className="flex items-center justify-between gap-2.5 w-full">
                    <div className="flex flex-1 gap-3">
                      {withFilePreview && !error && filePreviewUrl ? (
                        <div className="size-12 rounded-md flex items-center justify-center overflow-hidden">
                          <img
                            src={filePreviewUrl}
                            alt="File Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="size-12 bg-zinc-800 rounded-md flex items-center justify-center">
                          {error ? (
                            <WarningDiamondIcon
                              color="hsl(var(--destructive))"
                              size={28}
                            />
                          ) : (
                            fileIcon
                          )}
                        </div>
                      )}
                      <div className="flex flex-col gap-1 flex-1">
                        <p className="text-white line-clamp-1 flex-1">
                          {truncate(file.name, { length: 50 })}
                        </p>
                        <p className="text-zinc-400 text-sm">
                          {file.size > 0 && !isFileSizeEmpty
                            ? filesize(file.size)
                            : 'Unknown size'}
                        </p>
                      </div>
                    </div>

                    {isUploading ? (
                      <SpinnerIcon size={32} className="animate-spin" />
                    ) : (
                      <>
                        {error && !isUploading ? (
                          <Button
                            variant="outline"
                            data-testid="retry-button"
                            className="bg-transparent text-white"
                            onClick={handleRefresh}
                          >
                            <ArrowClockwiseIcon />
                            Retry
                          </Button>
                        ) : (
                          <TrashIcon
                            data-testid="remove-file-button"
                            size={32}
                            onClick={clearFile}
                          />
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2.5">
                      <SpinnerIcon size={32} className="animate-spin" />
                      <p className="text-white">Loading...</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-2.5">
                        <div className="size-12 bg-zinc-800 rounded-md flex items-center justify-center">
                          <PlusSquareIcon fill="white" size={36} />
                        </div>
                        <p className="text-white">{placeholder}</p>
                      </div>
                      <input
                        id={`dropzone-file-${name}`}
                        type="file"
                        className="hidden"
                        onChange={pickFile}
                        disabled={isUploading}
                        accept={accept.join(',')}
                        data-testid={testId}
                      />
                    </>
                  )}
                </>
              )}
            </label>
          </FormControl>

          <FormMessage errorMessage={error} />

          {tips}
        </FormItem>
      )}
    />
  );
};
